import { AskRequest } from '@hone-automation/common';
import { Attachment } from '../../../../../Queue/Chat/Chat';
import { CheckListItemProps } from '../../../../Common/CheckList/CheckList';

export type MessagePreviewExtraType = 'email' | 'arcade' | 'link' | 'attachments' | 'checklist' | 'details';

export interface MessageDetailsRow {
  label: string;
  value: string | number;
  money?: boolean;
  originalMoney?: number;
  highlight?: boolean;
}

export interface MessageDetailsSection {
  heading?: string;
  rows: MessageDetailsRow[];
}

export interface MessageDetailsLink {
  reportTitle?: string;
  sectionPath?: string;
  sectionAmount?: number;
  dataDateRange?: {
    start?: string;
    end?: string;
  };
  attachments?: Array<{ filename: string }>;
  valid?: boolean;
}

export interface MessageDetailsContent {
  title: string;
  requestType?: 'Ask Bookkeeper' | 'Ask Customer' | 'Flagged Cell' | 'Flagged Report' | 'Customer Request' | 'Email';
  sections: MessageDetailsSection[];
  footnote?: string;
  links?: MessageDetailsLink[];
  linksLoading?: boolean;
  linksError?: boolean;
}

export type EmailStatus = 'Error' | 'Processed' | 'Received' | 'Sent' | 'Waiting';

export interface EmailMetadata {
  messageId?: string;
  from?: string;
  to?: string;
  replyTo?: string;
  subject?: string;
  status?: EmailStatus;
}

export interface EmailPreviewExtra {
  type: 'email';
  html: string;
  emailHeaders: {
    to: string;
    from: string;
    subject: string;
  };
  isFirstMessage?: boolean;
  isAutoDetected?: boolean;
}

export interface ArcadePreviewExtra {
  type: 'arcade';
  url: string;
}

export interface LinkPreviewExtra {
  type: 'link';
  url: string;
}

export interface AttachmentsPreviewExtra {
  type: 'attachments';
  attachments: Attachment[];
}

export interface ChecklistPreviewExtra {
  type: 'checklist';
  checklist: CheckListItemProps[];
}

export interface DetailsPreviewExtra {
  type: 'details';
  data: MessageDetailsContent;
  request?: AskRequest;
  formatting?: MessageFormattingOptions;
}

export type MessagePreviewExtra =
  | EmailPreviewExtra
  | ArcadePreviewExtra
  | LinkPreviewExtra
  | AttachmentsPreviewExtra
  | ChecklistPreviewExtra
  | DetailsPreviewExtra;

export interface MessageContentAnalysis {
  extras: MessagePreviewExtra[];
  remainingMessage: string;
}

export interface MessageFormattingOptions {
  users?: any[];
  fallbackUser?: { userId: string; name: string };
}

export interface AnalyzeMessageContentOptions {
  attachments?: Attachment[];
  checklist?: CheckListItemProps[];
  details?: MessageDetailsContent;
  emailMetadata?: EmailMetadata;
  isFirstMessage?: boolean;
  request?: AskRequest;
  formatting?: MessageFormattingOptions;
}

const EMAIL_THREAD_REGEX = /class=["']gmail_/i;
const SPECIFIC_ARCADE_REGEX =
  /https:\/\/[\w-.~:/?#@!$&'()*+,;=%]+app\.arcade\.software\/share[\w-.~:/?#@!$&'()*+,;=%]*/i;
const GENERIC_URL_REGEX = /https?:\/\/[^\s<>"]+/gi;

const normalizeUrl = (url: string): string => url.replace(/["')>\s]+$/, '');

const extractUrlsFromHtml = (message: string): string[] => {
  if (!message) {
    return [];
  }

  if (typeof window !== 'undefined' && typeof window.DOMParser !== 'undefined') {
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(message, 'text/html');
      const anchors = Array.from(doc.querySelectorAll('a[href]')) as HTMLAnchorElement[];
      const hrefs = anchors.map(anchor => anchor.href.trim()).filter(href => /^https?:\/\//i.test(href));
      if (hrefs.length > 0) {
        return hrefs.map(normalizeUrl);
      }
    } catch (error) {
      // Swallow DOM parsing errors and fall back to regex detection
    }
  }

  const matches = message.match(GENERIC_URL_REGEX);
  return matches ? matches.map(normalizeUrl) : [];
};

/**
 * Checks if email metadata has any meaningful content.
 * Empty metadata objects should be treated as normal comments.
 */
const isValidEmailMetadata = (metadata?: EmailMetadata): boolean => {
  if (!metadata) return false;
  return !!(metadata.from || metadata.to || metadata.subject || metadata.status);
};

/**
 * Detects if a message is an email based on metadata or Gmail CSS classes.
 * Primary detection: emailMetadata presence (from comment.metadata.email)
 * Fallback: Gmail CSS class detection for pasted content formatting
 */
const detectEmailThread = (
  message: string,
  emailMetadata?: EmailMetadata,
  isFirstMessage?: boolean
): EmailPreviewExtra | null => {
  if (!message) {
    return null;
  }

  const emailHeaders = {
    from: emailMetadata?.from ?? '',
    to: emailMetadata?.to ?? '',
    subject: emailMetadata?.subject ?? '',
  };

  // Primary: If we have valid email metadata, this is an email
  if (isValidEmailMetadata(emailMetadata)) {
    return {
      type: 'email',
      html: message,
      emailHeaders,
      isFirstMessage,
    };
  }

  // Fallback: Detect Gmail CSS classes for pasted email content
  if (EMAIL_THREAD_REGEX.test(message)) {
    return {
      type: 'email',
      html: message,
      emailHeaders,
      isFirstMessage,
      isAutoDetected: true,
    };
  }

  return null;
};

const detectArcadeLink = (message: string): ArcadePreviewExtra | null => {
  if (!message) {
    return null;
  }

  if (typeof window !== 'undefined' && typeof window.DOMParser !== 'undefined') {
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(message, 'text/html');
      const anchor = doc.querySelector<HTMLAnchorElement>('a[href*="app.arcade.software/share"]');
      if (anchor?.href) {
        return { type: 'arcade', url: normalizeUrl(anchor.href) };
      }
    } catch (error) {
      // Swallow parsing errors and fall back to regex detection
    }
  }

  const match = message.match(SPECIFIC_ARCADE_REGEX);
  if (match) {
    return { type: 'arcade', url: normalizeUrl(match[0]) };
  }

  return null;
};

const detectFirstLink = (message: string): LinkPreviewExtra | null => {
  if (!message) {
    return null;
  }

  const urls = extractUrlsFromHtml(message);
  if (urls.length > 0) {
    return { type: 'link', url: urls[0] };
  }

  return null;
};

export const analyzeMessageContent = (
  message: string,
  options: AnalyzeMessageContentOptions = {}
): MessageContentAnalysis => {
  const extras: MessagePreviewExtra[] = [];
  let remainingMessage = message;

  // Add details extra only for non-custom request types
  // Custom Requests (Customer Request), Email, and Group requests don't need the details box
  const isCustomRequest = options.request?.type === 'Customer Request';
  const isEmailRequest = options.request?.type === 'Email';
  const isGroupRequest = options.request?.type === 'Group';
  if (options.details && !isCustomRequest && !isEmailRequest && !isGroupRequest) {
    extras.push({
      type: 'details',
      data: options.details,
      request: options.request,
      formatting: options.formatting,
    });
    // Keep the original message visible - it's now rendered separately above the details panel
  }

  if (options.checklist?.length) {
    extras.push({ type: 'checklist', checklist: options.checklist });
  }

  if (options.attachments?.length) {
    extras.push({ type: 'attachments', attachments: options.attachments });
  }

  if (!message) {
    return { extras, remainingMessage: '' };
  }

  const emailResult = detectEmailThread(message, options.emailMetadata, options.isFirstMessage);
  if (emailResult) {
    extras.push(emailResult);
    remainingMessage = '';
    return { extras, remainingMessage };
  }

  const arcadeResult = detectArcadeLink(message);
  if (arcadeResult) {
    extras.push(arcadeResult);
    return { extras, remainingMessage };
  }

  const linkResult = detectFirstLink(message);
  if (linkResult) {
    extras.push(linkResult);
  }

  return {
    extras,
    remainingMessage,
  };
};

export const messagePreviewDetectors = {
  detectEmailThread,
  detectArcadeLink,
  detectFirstLink,
  isValidEmailMetadata,
};
